import { Undo2 } from "lucide-react";
import PropTypes from "prop-types";
import StatusChip from "@components/ui-components/status-chip";
import { formatDate } from "@helpers/date";
import { formatCurrency } from "@helpers/utils";
import { KYC_LABEL, KYC_TONE, INVOICE_STATUS_TONE, INVOICE_STATUS_LABEL } from "@constants/panel/common";
import { CONTRACT_STATUS, CONTRACT_STATUS_LABEL, CONTRACT_STATUS_TONE } from "@panel/shared/contract/misc/constants";

const kycLabelLower = (kycStatus) => (KYC_LABEL[kycStatus] || kycStatus || "").toLowerCase();
const contractLabelLower = (contractStatus) => (CONTRACT_STATUS_LABEL[contractStatus] || contractStatus || "").toLowerCase();

const isPayable = (inv) => inv.kyc_status === "verified" && inv.contract_status === CONTRACT_STATUS.ACKNOWLEDGED;

const holdReason = (inv) => {
    const reasons = [];
    if (inv.kyc_status !== "verified") {
        reasons.push(`KYC ${kycLabelLower(inv.kyc_status)}`);
    }
    if (inv.contract_status !== CONTRACT_STATUS.ACKNOWLEDGED) {
        reasons.push(`Contract ${contractLabelLower(inv.contract_status)}`);
    }
    return reasons.join(" · ");
};

const InvoiceIdentity = ({ inv }) => (
    <div className="flex items-center gap-3">
        <span className="font-medium text-gray-900">{inv.holder_name}</span>
        <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs text-gray-600">{inv.invoice_number}</span>
        <StatusChip tone={KYC_TONE[inv.kyc_status] || "neutral"} label={KYC_LABEL[inv.kyc_status] || inv.kyc_status} />
        <StatusChip tone={CONTRACT_STATUS_TONE[inv.contract_status] || "neutral"} label={CONTRACT_STATUS_LABEL[inv.contract_status] || inv.contract_status} />
    </div>
);

InvoiceIdentity.propTypes = {
    inv: PropTypes.object.isRequired
};

const DisbursementInvoices = ({ item, onRecordPayment, onRevertPayment }) => {
    const invoices = item.invoices || [];
    const payableInvoices = invoices.filter(isPayable);
    const heldInvoices = invoices.filter((inv) => !isPayable(inv));

    if (invoices.length === 0) {
        return <p className="text-sm text-muted">No publisher lines on this debit note.</p>;
    }

    return (
        <div className="space-y-4">
            {heldInvoices.length > 0 && (
                <div className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5 text-sm text-gray-700">
                    {heldInvoices.length} line{heldInvoices.length > 1 ? "s" : ""} held — this debit note cannot close:{" "}
                    <span className="font-medium text-accent">
                        {heldInvoices.map((inv) => `${inv.holder_name} · ${formatCurrency(inv.amount, inv.currency)} · ${holdReason(inv)}`).join(", ")}
                    </span>
                </div>
            )}

            {payableInvoices.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Payable</p>
                    <div className="divide-y divide-gray-100">
                        {payableInvoices.map((inv) => (
                            <div key={inv._id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                                <InvoiceIdentity inv={inv} />
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-gray-900">{formatCurrency(inv.amount, inv.currency)}</span>
                                    <StatusChip tone={INVOICE_STATUS_TONE[inv.status] || "neutral"} label={INVOICE_STATUS_LABEL[inv.status] || inv.status} />
                                    {inv.status === "paid" ? (
                                        <>
                                            <span className="text-xs text-muted">{[inv.payment_reference, inv.paid_at ? formatDate(inv.paid_at) : null].filter(Boolean).join(" · ")}</span>
                                            <button
                                                type="button"
                                                onClick={() => onRevertPayment(inv)}
                                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                                                <Undo2 className="h-3.5 w-3.5" />
                                                Revert payment
                                            </button>
                                        </>
                                    ) : ["eligible", "reverted", "pending"].includes(inv.status) && (
                                        <button
                                            type="button"
                                            disabled={inv.eligibility !== "eligible"}
                                            title={inv.eligibility !== "eligible" ? "Publisher isn't eligible for payout yet" : ""}
                                            onClick={() => onRecordPayment(inv)}
                                            className="rounded-lg bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50">
                                            Record Payment
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {heldInvoices.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Held for KYC / Contract</p>
                    <div className="divide-y divide-gray-100">
                        {heldInvoices.map((inv) => (
                            <div key={inv._id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                                <InvoiceIdentity inv={inv} />
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-gray-900">{formatCurrency(inv.amount, inv.currency)}</span>
                                    <span className="text-xs text-muted">{holdReason(inv)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

DisbursementInvoices.propTypes = {
    item           : PropTypes.object.isRequired,
    onRecordPayment: PropTypes.func.isRequired,
    onRevertPayment: PropTypes.func.isRequired
};

export default DisbursementInvoices;
